import { useCallback, useMemo } from 'react';

import { logger } from '../../../logger';
import { getModifierConfiguration } from '../modifiers';
import * as types from '../types';

type ExtendedProps = {
  [key in keyof types.CustomModifierApplicableProps]-?:
    | null
    | types.CustomModifierApplicableProps[key];
};

/**
 * Indicates whether or not two custom modifiers, {@link types.CustomModifier}, or a custom modifier
 * and a custom modifier name, {@link types.CustomModifierName}, are equivalent - indicating that
 * they represent the same custom modifier based on the name.
 */
const areModifiersEquivalent = (
  modifier1: types.CustomModifier | types.CustomModifierName,
  modifier2: types.CustomModifier | types.CustomModifierName,
): boolean =>
  /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This is safe to
     suppress because the reason it is surfacing is because there is currently only one custom
     modifier - but the logic should remain for purposes of supporting additional custom modifiers
     in the future.  */
  (typeof modifier1 === 'string' ? modifier1 : modifier1.name) ===
  (typeof modifier2 === 'string' ? modifier2 : modifier2.name);

export interface UseModifiersReturn {
  /**
   * A method that can be used to determine whether or not a custom modifier,
   * {@link types.CustomModifier}, with the provided name, {@link types.CustomModifierName},
   * has been applied.
   */
  readonly hasCustomModifier: (name: types.CustomModifierName) => boolean;
  /**
   * An array of all of the modifiers, {@link types.MuiModifier}, that should be applied to the
   * {@link Popper} component.  This includes both native MUI modifiers provided directly to the
   * {@link Popper} component and flattened out children of custom modifiers,
   * {@link types.CustomModifier}, as well.
   */
  readonly modifiers: types.MuiModifier[];
}

export interface UseModifiersParams extends ExtendedProps {
  /**
   * An optional prop that should only be provided when the {@link Popper} component is being
   * used as a child of another component that also uses the {@link useModifiers} hook, in order to
   * allow sharing of the computed modifiers between the parent and child components, avoiding
   * the unnecessary computation of the same modifiers multiple times.
   */
  readonly __privateUseModifiersParentScope__?: UseModifiersReturn;
  /**
   * An optional array of modifiers, both native ({@link types.MuiModifier}) and custom
   * ({@link types.CustomModifier}), that are applied to the {@link Popper}.
   *
   * @see {@link types.Modifier}
   * @see {@link types.CustomModifier}
   */
  readonly modifiers?: types.ModifiersArray<types.Modifier>;
}

/**
 * Represents the native, MUI-compatible modifier, {@link types.MuiModifier}, that was either
 * provided directly inside of the 'modifiers' prop provided to the {@link Popper} component, or
 * was the result of flattening out the children of a custom modifier, {@link types.CustomModifier}.
 *
 * If the modifier was the result of flattening out the children of a custom modifier, this type
 * also includes a reference to the custom modifier that produced it, {@link types.CustomModifier},
 * as well.
 */
type ModifierAndSource = {
  readonly custom?: types.CustomModifier;
  readonly modifier: types.MuiModifier;
};

/**
 * A hook responsible for computing the complete set of modifiers, {@link types.MuiModifier}, that
 * should be applied to the native MUI {@link Popper} component, based on the modifiers provided
 * to the internal {@link Popper} component, which contain both native MUI modifiers and custom
 * modifiers, {@link types.CustomModifier}, as well as any relevant props that may be needed by
 * custom modifiers.
 */
export const useModifiers = (params: UseModifiersParams): UseModifiersReturn => {
  const modifiersWithSource = useMemo<ModifierAndSource[]>(() => {
    if (params.__privateUseModifiersParentScope__) {
      return [];
    }

    const addNativeModifier = (
      source: ModifierAndSource[],
      modifier?: types.MuiModifier,
    ): ModifierAndSource[] => {
      if (!modifier) {
        return source;
      }
      const existing = source.find(m => m.modifier.name === modifier.name);
      if (existing?.custom !== undefined) {
        logger.warn(
          `Popper: Encountered a modifier with name '${modifier.name}' that was already ` +
            `applied as a child of a custom modifier, '${existing.custom.name}'.  This ` +
            "modifier will be ignored, and the custom modifier's child will take precedence.",
          { existingModifier: existing, modifier, modifiers: params.modifiers },
        );
        return source;
      } else if (existing) {
        logger.warn(
          `Popper: Encountered a modifier with name '${modifier.name}' that was already ` +
            'applied earlier in the array of modifiers.  This modifier will take precedence, ' +
            'and the earlier one will be ignored.',
          { existingModifier: existing, modifier, modifiers: params.modifiers },
        );
        return [
          ...source.filter(m => m.modifier.name !== modifier.name),
          { modifier: { ...modifier, enabled: modifier.enabled ?? true } },
        ];
      }
      return [...source, { modifier: { ...modifier, enabled: modifier.enabled ?? true } }];
    };

    const sourced = (params.modifiers ?? []).reduce((acc: ModifierAndSource[], modifier) => {
      if (!modifier) {
        return acc;
      } else if (!modifier.isCustom) {
        return addNativeModifier(acc, modifier);
      }
      const configuration = getModifierConfiguration(modifier.name);

      const props = {
        maxHeight: params.maxHeight,
        offset: params.offset,
        placement: params.placement,
      };

      const children = configuration.getChildren(modifier, props);
      if (children.length === 0) {
        return acc;
      }

      /* Note: Situations where a custom modifier and a native modifier have the same name are
         not dangerous as the custom modfier's name is only used for identification within the
         context of custom modifiers, and its name is not referenced after its configuration
         is evaluated. */
      const existing = acc.find(m => m.custom && areModifiersEquivalent(m.custom, modifier));
      if (existing) {
        logger.warn(
          'Popper: Encountered duplicate custom modifiers of the same name, ' +
            `'${modifier.name}'. Only the latter one will be applied, and the earlier one ` +
            'will be ignored.',
          { existingModifier: existing, modifier, modifiers: params.modifiers },
        );
      }

      configuration.logPropCollisions(props);

      return children.reduce(
        (childAcc: ModifierAndSource[], child) => {
          const existingChild = childAcc.find(
            mod => !mod.custom && mod.modifier.name === child.name,
          );
          if (existingChild) {
            if (child.settings.conflictResolution) {
              child.logConflict(existingChild.modifier);

              switch (child.settings.conflictResolution) {
                case types.ModifierConflictResolution.Avoid: {
                  return childAcc;
                }
                case types.ModifierConflictResolution.Overwrite: {
                  return [
                    ...childAcc.filter(
                      m => m.custom !== undefined || m.modifier.name !== child.name,
                    ),
                    { custom: modifier, modifier: child.native },
                  ];
                }
                default:
                  child.settings.conflictResolution satisfies never;
              }
            } else {
              child.logWarning(
                'A modifier with this name was already applied but the child modifier did not ' +
                  'define a conflict resolution setting.  Both modifiers will be included in ' +
                  'the resulting array.',
              );
            }
          }
          return [...childAcc, { custom: modifier, modifier: child.native }];
        },
        acc.filter(m => !m.custom || !areModifiersEquivalent(m.custom, modifier)),
      );
    }, []);

    return addNativeModifier(
      sourced,
      params.offset ? { name: 'offset', options: { offset: params.offset } } : undefined,
    );
  }, [
    params.modifiers,
    params.__privateUseModifiersParentScope__,
    params.maxHeight,
    params.offset,
    params.placement,
  ]);

  const hasCustomModifier = useCallback(
    (name: types.CustomModifierName) =>
      modifiersWithSource.some(
        mod => mod.custom !== undefined && areModifiersEquivalent(mod.custom, name),
      ),
    [modifiersWithSource],
  );

  const modifiers = useMemo(
    () =>
      params.__privateUseModifiersParentScope__?.modifiers ??
      modifiersWithSource.map(m => m.modifier),
    [modifiersWithSource, params.__privateUseModifiersParentScope__?.modifiers],
  );

  return {
    hasCustomModifier:
      params.__privateUseModifiersParentScope__?.hasCustomModifier ?? hasCustomModifier,
    modifiers,
  };
};
